import { useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import {
  getStudentNotifications,
  markNotificationRead,
} from "../../../../services/StudentPortal/StudentNotificationService";
import { unwrapList } from "../../utils/studentPortalUtils";
import { handleApiError } from "../../../../utils/errorHandler";

export const useStudentNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMarking, setIsMarking] = useState(false);

  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getStudentNotifications();
      setNotifications(unwrapList(response));
    } catch (error) {
      handleApiError(error);
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Confirmed against the shipping mobile app's API client (2026-10-22):
  // mark-read only takes one notification_id per call — there's no bulk
  // "mark all" endpoint, so this marks every unread one individually.
  const markAllRead = useCallback(async () => {
    if (!notifications.length) return;
    setIsMarking(true);
    try {
      await Promise.all(
        notifications
          .filter((notification) => notification.id !== undefined && notification.id !== null)
          .map((notification) => markNotificationRead(notification.id))
      );
      enqueueSnackbar("All notifications marked as read", { variant: "success" });
      await fetchNotifications();
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsMarking(false);
    }
  }, [notifications, fetchNotifications]);

  return { notifications, isLoading, isMarking, fetchNotifications, markAllRead };
};

export default useStudentNotifications;
